import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import gdriveIcon from 'assets/images/gdrive.svg';
import msIcon from 'assets/images/social_media_ms.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import postgresIcon from 'assets/images/postgres.svg';
import mongodbIcon from 'assets/images/mongodb.svg';
import mysqlIcon from 'assets/images/mysql.svg';
import databaseIcon from 'assets/images/database.svg';
import ftpIcon from 'assets/images/ftp.svg';
import { GoogleDriveConnection } from './google-drive-connection';
import { SharePointConnection } from './sharepoint-connection';
import { OneDriveConnection } from './onedrive-connection';
import { PostgreSQLConnection } from './postgresql-connection';
import { MongoDBConnection } from './mongodb-connection';
import { MySQLConnection } from './mysql-connection';
import { SFTPConnection } from './sftp-connection';
import { useIntegrationToken } from 'hooks/use-integration-token';
import { useGetIntegrations } from 'hooks/use-get-integrations';
import { useIntegrationsStore } from 'state/store/integrations/useIntegrationsStore';
import { useListDatabaseConnections } from 'hooks/use-list-database-connections';
import { IntegrationAppProvider } from '@membranehq/react';
import { useConfig } from 'hooks/use-config';

/**
 * Connectors Tab Component
 * 
 * Manages external integrations and connectors for the application.
 * Provides interface to view, add, configure, and manage various connectors.
 * 
 * Features:
 * - List of available and connected integrations
 * - Connection status indicators
 * - Add new connector functionality
 * - Enable/disable connectors
 * - Configure existing connectors
 * 
 * @param tabId - The ID of the tab
 * @param selectedConnectorId - The ID of the connector to auto-open
 * @param onCloseModal - Callback to close the settings modal
 * @returns A connectors management interface
 */

interface ConnectorsTabProps {
    tabId: string;
    selectedConnectorId?: string | null;
    onCloseModal?: () => void;
}

interface Connector {
    id: string;
    name: string;
    description: string;
    iconUrl: string;
    isAvailable: boolean;
}

interface DatabaseType {
    id: string;
    name: string;
    description: string;
    isAvailable: boolean;
}

// SVG Icons as components
const GoogleDriveIcon = () => (
    <img src={gdriveIcon} alt="Google Drive" width="24" height="24" />
);

const SharePointIcon = () => (
    <img src={msIcon} alt="Microsoft SharePoint" width="24" height="24" />
);

const OneDriveIcon = () => (
    <img src={onedriveIcon} alt="Microsoft OneDrive" width="28" height="28" />
);

// Database Icon Component
const DatabaseIcon = () => (
    <img src={databaseIcon} alt="Database" width="24" height="24" />
);

// PostgreSQL Icon Component
const PostgreSQLIcon = () => (
    <img src={postgresIcon} alt="PostgreSQL" width="24" height="24" className="object-contain" />
);

// MongoDB Icon Component
const MongoDBIcon = () => (
    <img src={mongodbIcon} alt="MongoDB" width="24" height="24" className="object-contain" />
);

// MySQL Icon Component
const MySQLIcon = () => (
    <img src={mysqlIcon} alt="MySQL" width="24" height="24" className="object-contain" />
);

// SFTP Icon Component
const SFTPIcon = () => (
    <img src={ftpIcon} alt="SFTP" width="24" height="24" className="object-contain" />
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ConnectorsTab = ({ tabId, selectedConnectorId, onCloseModal }: ConnectorsTabProps) => {
    const { t } = useTranslation();
    const [selectedConnector, setSelectedConnector] = useState<string | null>(null);
    const [cameFromExternalApps, setCameFromExternalApps] = useState(false);
    const { token, isLoadingToken } = useIntegrationToken();
    const { data: integrations, isLoading: isLoadingIntegrations, error, isError } = useGetIntegrations();
    const { getIntegrationByType, isConnected } = useIntegrationsStore();
    const { configurationSettings } = useConfig();
    const { data: dbConnectionsList } = useListDatabaseConnections();

    // Debug logging
    // eslint-disable-next-line no-console
    console.log('ConnectorsTab - integrations:', integrations);
    // eslint-disable-next-line no-console
    console.log('ConnectorsTab - isLoadingIntegrations:', isLoadingIntegrations);
    // eslint-disable-next-line no-console
    console.log('ConnectorsTab - error:', error);
    // eslint-disable-next-line no-console
    console.log('ConnectorsTab - isError:', isError);
    // eslint-disable-next-line no-console
    console.log('ConnectorsTab - dbConnectionsList:', dbConnectionsList);

    const [connectors] = useState<Connector[]>([
        {
            id: 'google-drive',
            name: 'Google Drive',
            description: 'ACCESS_AND_SEARCH_YOUR_GOOGLE_DRIVE_FILES',
            iconUrl: '', // We'll use the SVG component instead
            isAvailable: false
        },
        /* {
            id: 'microsoft-sharepoint',
            name: 'Microsoft Sharepoint',
            description: 'Access and search your Sharepoint files',
            iconUrl: '', // We'll use the SVG component instead
            isAvailable: true
        }, */
        {
            id: 'onedrive',
            name: 'Microsoft OneDrive',
            description: 'ACCESS_AND_SEARCH_YOUR_ONEDRIVE_FILES',
            iconUrl: '', // We'll use the SVG component instead
            isAvailable: true
        },
        {
            id: 'database',
            name: 'Database',
            description: 'CONNECT_TO_YOUR_DATABASES',
            iconUrl: '', // We'll use the SVG component instead
            isAvailable: true
        },
        {
            id: 'sftp',
            name: 'SFTP',
            description: 'CONNECT_TO_SFTP_SERVER',
            iconUrl: '', // We'll use the SVG component instead
            isAvailable: true
        },
    ]);

    const [databases] = useState<DatabaseType[]>([
        {
            id: 'postgresql',
            name: 'PostgreSQL',
            description: 'CONNECT_TO_POSTGRESQL_DATABASE',
            isAvailable: true
        },
        {
            id: 'mongodb',
            name: 'MongoDB',
            description: 'CONNECT_TO_MONGODB_DATABASE',
            isAvailable: false
        },
        {
            id: 'mysql',
            name: 'MySQL',
            description: 'CONNECT_TO_MYSQL_DATABASE',
            isAvailable: false
        }
    ]);

    useEffect(() => {
        if (selectedConnectorId) {
            setSelectedConnector(selectedConnectorId);
            setCameFromExternalApps(true);
        }
    }, [selectedConnectorId]);

    if (isLoadingToken || isLoadingIntegrations) {
        return (
            <div className="h-[560px] bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    const getIconComponent = (connectorId: string) => {
        switch (connectorId) {
            case 'google-drive':
                return <GoogleDriveIcon />;
            case 'microsoft-sharepoint':
                return <SharePointIcon />;
            case 'onedrive':
                return <OneDriveIcon />;
            case 'sftp':
                return <SFTPIcon />;
            case 'database':
                return <DatabaseIcon />;
            default:
                return <GoogleDriveIcon />;
        }
    };

    const getDatabaseIconComponent = (databaseId: string) => {
        switch (databaseId) {
            case 'postgresql':
                return <PostgreSQLIcon />;
            case 'mongodb':
                return <MongoDBIcon />;
            case 'mysql':
                return <MySQLIcon />;
            default:
                return <DatabaseIcon />;
        }
    };

    // Helper function to check if a connector is connected (using store method)
    const getConnectedIntegration = (connectorId: string) => {
        return getIntegrationByType(connectorId);
    };

    const handleConnectorClick = (id: string) => {
        setSelectedConnector(id);
    };

    const handleBackToConnectors = () => {
        // If came from external apps, close the modal
        if (cameFromExternalApps && onCloseModal) {
            onCloseModal();
        } else {
            // Otherwise, go back to connectors list
            setSelectedConnector(null);
        }
    };

    const handleBackToDatabaseList = () => {
        setSelectedConnector('database');
    };

    // Show specific connector connection UI if one is selected
    if (selectedConnector === 'google-drive') {
        const connectedIntegration = getConnectedIntegration('google-drive');
        return (
            <IntegrationAppProvider token={token} apiUri={configurationSettings.paths.integrationAppApiUri} uiUri={configurationSettings.paths.integrationAppUiUri}>
                <GoogleDriveConnection
                    onBack={handleBackToConnectors}
                    existingIntegration={connectedIntegration}
                />
            </IntegrationAppProvider>
        );
    }

    if (selectedConnector === 'microsoft-sharepoint') {
        const connectedIntegration = getConnectedIntegration('microsoft-sharepoint');
        return (
            /// todo:  need to add apiUri and uiUri
            <IntegrationAppProvider token={token} apiUri={configurationSettings.paths.integrationAppApiUri} uiUri={configurationSettings.paths.integrationAppUiUri}>
                <SharePointConnection
                    onBack={handleBackToConnectors}
                    existingIntegration={connectedIntegration}
                />
            </IntegrationAppProvider>
        );
    }

    if (selectedConnector === 'onedrive') {
        const connectedIntegration = getConnectedIntegration('onedrive');
        return (
            <IntegrationAppProvider token={token} apiUri={configurationSettings.paths.integrationAppApiUri} uiUri={configurationSettings.paths.integrationAppUiUri}>
                <OneDriveConnection
                    onBack={handleBackToConnectors}
                    existingIntegration={connectedIntegration}
                />
            </IntegrationAppProvider>
        );
    }

    if (selectedConnector === 'sftp') {
        const connectedIntegration = getConnectedIntegration('sftp');
        return (
            <SFTPConnection
                onBack={handleBackToConnectors}
                existingIntegration={connectedIntegration}
            />
        );
    }

    // Show PostgreSQL connection component if selected
    if (selectedConnector === 'postgresql') {
        return (
            <PostgreSQLConnection
                onBack={handleBackToDatabaseList}
            />
        );
    }

    // Show MongoDB connection component if selected
    if (selectedConnector === 'mongodb') {
        return (
            <MongoDBConnection
                onBack={handleBackToDatabaseList}
            />
        );
    }

    // Show MySQL connection component if selected
    if (selectedConnector === 'mysql') {
        return (
            <MySQLConnection
                onBack={handleBackToDatabaseList}
            />
        );
    }

    // Show database list view if database connector is selected
    if (selectedConnector === 'database') {
        return (
            <div className="h-full overflow-y-auto">
                <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 space-y-6 sm:space-y-8 max-w-4xl">
                    {/* Header Section with Back Button */}
                    <div className="space-y-2">
                        <button
                            onClick={handleBackToConnectors}
                            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t('BACK') || 'Back'}
                        </button>
                        <h2 className="text-lg sm:text-xl font-semibold text-foreground">
                            {t('DATABASES') || 'Databases'}
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {t('SELECT_DATABASE_TYPE') || 'Select a database type to connect'}
                        </p>
                    </div>

                    {/* Databases Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {databases.filter(database => database.isAvailable).map((database) => {
                            // Check if this database type has an active connection
                            const activeConnection = dbConnectionsList?.find(
                                conn => conn.DatabaseType === database.id && conn.IsActive
                            );
                            const showConnected = database.id === 'postgresql' && !!activeConnection;

                            return (
                                <button
                                    key={database.id}
                                    onClick={() => {
                                        // Navigate to specific database configuration page
                                        setSelectedConnector(database.id);
                                    }}
                                    className="flex flex-col items-start gap-2 sm:gap-3 p-3 sm:p-4 border border-light-gray rounded-lg hover:border-border transition-colors cursor-pointer bg-background min-h-[90px] sm:min-h-auto"
                                >
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0">
                                        {getDatabaseIconComponent(database.id)}
                                    </div>
                                    <div className="text-left flex-1 w-full">
                                        <p className="text-sm sm:text-base font-bold text-foreground leading-[1.4]">
                                            {database.name}
                                        </p>
                                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 leading-[1.4]">
                                            {t(database.description)}
                                        </p>
                                        {showConnected && (
                                            <div className="flex items-center gap-1 mt-1.5 sm:mt-2">
                                                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full"></div>
                                                <span className="text-[10px] sm:text-xs text-muted-foreground">{t('CONNECTED')}</span>
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    // Default connectors list view
    return (
        <div className="h-full overflow-y-auto">
            <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 space-y-6 sm:space-y-8 max-w-4xl">
                {/* Header Section */}
                <div className="space-y-2">
                    <h2 className="text-lg sm:text-xl font-semibold text-foreground">
                        {t('CONNECTORS') || 'Connectors'}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                        {t('CONNECT_YOUR_FAVORITE_APPS')}
                    </p>
                </div>

                {/* Connectors Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {connectors.filter(connector => connector.isAvailable).map((connector) => {
                        const integration = getIntegrationByType(connector.id);

                        // For SFTP, check is_active flag; for others, just check if integration exists
                        const connectorIsConnected = connector.id === 'sftp'
                            ? (integration?.is_active === true)
                            : isConnected(connector.id);

                        return (
                            <button
                                key={connector.id}
                                onClick={() => handleConnectorClick(connector.id)}
                                className="flex flex-col items-start gap-2 sm:gap-3 p-3 sm:p-4 border border-light-gray rounded-lg hover:border-border transition-colors cursor-pointer bg-background min-h-[90px] sm:min-h-auto"
                            >
                                <div className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0">
                                    {getIconComponent(connector.id)}
                                </div>
                                <div className="text-left flex-1 w-full">
                                    <p className="text-sm sm:text-base font-bold text-foreground leading-[1.4]">
                                        {connector.name}
                                    </p>
                                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 leading-[1.4]">
                                        {t(connector.description)}
                                    </p>
                                    {connectorIsConnected && (
                                        <div className="flex items-center gap-1 mt-1.5 sm:mt-2">
                                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-[10px] sm:text-xs text-muted-foreground">{t('CONNECTED')}</span>
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};